import React from 'react';
import { connect } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';

import { getFlagUrl } from '../../../utils';
import { titleCase, formatMatchDateForEventPage, formatMatchTime } from '../../../utils/formatting';
import { shouldDisplayRoundNumbers, EventTypes } from '../../../utils/events';
import { renderHSInfo } from '../../../utils/draws';
import Utr from '../../text/Utr';
import BackgroundImage from '../../BackgroundImage';
import Img from '../../Img';
import PhoneInfoPopup from '../../PopUps/PhoneInfoPopup';

import styles from './scoreCardBlank.scss';

const ScoreCardBlank = (props) => {
    const {
        position1,
        position2,
        position1Seed,
        position2Seed,
        playersDict,
        teamsDict,
        date,
        gameType,
        roundNumber,
        roundSize,
        round,
        matchPosition,
        timeZoneValue,
        error,
        detail,
        eliminationType,
        showDecimals,
        utrType,
        showDate,
        matchId,
        events,
        displayTime,
        contacts,
        showContacts,
        auth,
        isProProfile,
        ratingsNoShow,
        isOfficialHSEvent,
        ballType,
        sportTypeId,
        'data-testid': dataTestId,
    } = props;

    const event = events?.currentEvent?.data;
    const scheduleType = event?.scheduleType?.value || 'matchsheet';
    const { matchSheet, orderOfPlay } = events?.currentEvent || {};
    const isFlexLeagueEvent = event?.eventType?.id === EventTypes.FLEX_LEAGUE;
    let locationDisplay = detail;

    const user = auth && auth.user;
    const showRatings = (user || isProProfile) && !ratingsNoShow;
    const isProgressionDraw = ballType !== null;

    let infoObj;
    let locationName;
    let facilityName;
    let courtName;
    if (scheduleType === 'matchsheet' && matchSheet && matchSheet.length) {
        const matchSheetIndex = matchSheet.findIndex(m => m.matchId === matchId);
        if (matchSheetIndex > -1) {
            infoObj = matchSheet[matchSheetIndex];
        }
    }
    if (scheduleType === 'orderofplay' && orderOfPlay && orderOfPlay.length) {
        const oopIndex = orderOfPlay.findIndex(m => m.matchId === matchId);
        if (oopIndex > -1) {
            infoObj = orderOfPlay[oopIndex];
        }
    }
    if (infoObj) {
        locationDisplay = null;
        if (infoObj.locationId || infoObj.facilityId || infoObj.courtId) {
            (events.currentEvent.locations || []).forEach(loc => {
                if (loc.id === infoObj.locationId) {
                    locationName = loc.name;
                    loc.facilities.forEach(fac => {
                        if (fac.id === infoObj.facilityId) {
                            facilityName = fac.abbreviation || fac.name;
                            fac.courts.forEach(c => {
                                if (c.id === infoObj.courtId) {
                                    courtName = c.name;
                                }
                            });
                        }
                    });
                }
            });
            if (!locationName) {
                locationDisplay = 'Loading...';
            } else {
                locationDisplay = [facilityName, courtName].filter(Boolean).join(', ');
            }
        }
    }

    const getContactObj = (mId) => {
        return (contacts || []).find(({ memberId }) => memberId === mId);
    };

    const isCurrentUserRegistered = (memberId) => {
        return user?.id === memberId;
    };

    const matchPostScore = () => {
        props.history.push({ pathname: `/player/post-score`, search: `matchId=${matchId}` });
    };

    const hasBye = (position1 && position1.type && position1.type.value === 'bye') || (position2 && position2.type && position2.type.value === 'bye');

    const renderRoundLabel = () => {
        if (error) {
            return <span data-testid={dataTestId && `${dataTestId}.round-label`}>Please correct score</span>;
        }
        if (eliminationType && eliminationType === 'match_play') {
            return <span data-testid={dataTestId && `${dataTestId}.round-label`}>Match {matchPosition}</span>;
        }
        if (shouldDisplayRoundNumbers(eliminationType)) {
            return <span data-testid={dataTestId && `${dataTestId}.round-label`}>R{roundNumber}</span>;
        }
        if (roundSize === 1) {
            return <span data-testid={dataTestId && `${dataTestId}.round-label`}>F</span>;
        }
        if (roundSize === 2) {
            return <span data-testid={dataTestId && `${dataTestId}.round-label`}>SF • Match {matchPosition}</span>;
        }
        if (roundSize === 4) {
            return <span data-testid={dataTestId && `${dataTestId}.round-label`}>QF • Match {matchPosition}</span>;
        }
        return <span data-testid={dataTestId && `${dataTestId}.round-label`}>R{roundSize * 2} • Match {matchPosition}</span>;
    };

    const getPlayer = (position) => {
        let player; let team; let player1; let player2;
        const isPlayer = position && position.type && position.type.value === 'player';
        const isTeam = position && position.type && position.type.value === 'team';

        if (position) {
            if (isPlayer) {
                player = playersDict[position.id];
                return player;
            }
            if (isTeam) {
                team = teamsDict[position.id];
                player1 = team && playersDict[team.players[0]];
                player2 = team && playersDict[team.players[1]];
                return [player1, player2];
            }
        }
    };

    const getDrawTag = (position) => {
        let player; let team;
        const isPlayer = position && position.type && position.type.value === 'player';
        const isTeam = position && position.type && position.type.value === 'team';

        if (position && position.id != null) {
            if (isPlayer) {
                player = playersDict[position.id];
                return player?.draws?.[round.draw]?.tag?.value;
            }
            if (isTeam) {
                team = teamsDict[position.id];
                return team?.draws?.[round.draw]?.tag?.value;
            }
        }
        return <>&nbsp;</>;
    };

    const canPostScore = () => {
        let playersList = [];
        if (!isFlexLeagueEvent || !position1 || !position2 || hasBye) {
            return false;
        }

        if (gameType === 'singles') {
            playersList.push(getPlayer(position1));
            playersList.push(getPlayer(position2));
        } else {
            const player1 = getPlayer(position1);
            const player2 = getPlayer(position2);
            playersList = [...player1, ...player2];
        }

        return playersList.findIndex(l => l?.memberId === user?.id) !== -1;
    };

    const getCardTeam = (position, seed) => {
        let player; let player1; let player2;

        const isPlayer = position?.type?.value === 'player';
        const isTeam = position?.type?.value === 'team';
        const isBye = position?.type?.value === 'bye';

        if (position) {
            if (isPlayer) {
                player = getPlayer(position);
            }
            if (isTeam) {
                [player1, player2] = getPlayer(position);
            }
        }

        return (
            <div className="team">
                <span className="seed" data-testid={dataTestId && `${dataTestId}.position-${position?.id}.seed`}>
                    {seed}
                </span>
                <div className={`flex-column player-details-container ml0`}>
                    {isPlayer && player && player.id && (
                        <div className={`player-details overflow ${styles.overflow}`}>
                            {!!player.clubProfileImages && (
                                <BackgroundImage
                                    baseUrl={'/v1/club/'}
                                    defaultImage={'/img/utr-ball--teal.svg'}
                                    images={player.clubProfileImages}
                                    imageType={'thumbnail'}
                                    parentStylesClass={'flag-container club-profile-image'}
                                />
                            )}
                            {!player.clubProfileImages && (
                                <span className="flag-container">
                                    {player.nationality && (
                                        <Img
                                            src={getFlagUrl(player.nationality)}
                                            className="flag flagStatsAlign"
                                            alt={player.nationality}
                                        />
                                    )}
                                </span>
                            )}
                            <div className={`d-flex flex-column justify-content-start align-items-start player-name name ${styles.name}`}>
                                <Link to={`/profiles/${player.id}`} className={styles.link} data-testid={dataTestId && `${dataTestId}.position-${position?.id}.player1-link`}>
                                    <div className="d-flex flex-row align-items-baseline">
                                        <div className="p0 mb0 name-wrapper show-ellipsis" data-testid={dataTestId && `${dataTestId}.position-${position?.id}.player1-name`}>
                                            {titleCase(player.displayName)}
                                        </div>
                                        {showContacts && getContactObj(player.memberId) && <PhoneInfoPopup {...getContactObj(player.memberId)} playerId={player.id} showEditContacts={isCurrentUserRegistered(player.memberId)} />}
                                        {showRatings && (
                                            <div className="utr-container">
                                                <Utr pbrClass={styles.pbr} player={player} isProgressionDraw={isProgressionDraw} force={{ ratingType: gameType, utrType, showDecimals }} sportTypeId={sportTypeId} />
                                            </div>
                                        )}
                                    </div>
                                </Link>
                                {!!isOfficialHSEvent && <div className="hs-info" data-testid={dataTestId && `${dataTestId}.position-${position?.id}.player1-hs-info`}>{renderHSInfo(player)}</div>}
                            </div>
                        </div>
                    )}
                    {isTeam && player1 && player1.id && player2 && player2.id && (
                        <div className="player-details">
                            <div className={`player-name d-flex flex-column`}>
                                <Link to={`/profiles/${player1.id}`} data-testid={dataTestId && `${dataTestId}.position-${position?.id}.player1-link`}>
                                    <div className="d-flex flex-row align-items-baseline">
                                        <div className="name-wrapper" data-testid={dataTestId && `${dataTestId}.position-${position?.id}.player1-name`}>
                                            {titleCase(`${player1.firstName[0]}. ${player1.lastName}`)}
                                        </div>
                                        {showContacts && getContactObj(player1.memberId) && <PhoneInfoPopup {...getContactObj(player1.memberId)} playerId={player1.id} showEditContacts={isCurrentUserRegistered(player1.memberId)} />}
                                        {showRatings && (
                                            <div className="utr-container">
                                                <Utr pbrClass={styles.pbr} player={player1} isProgressionDraw={isProgressionDraw} force={{ ratingType: gameType, utrType, showDecimals }} sportTypeId={sportTypeId} />
                                            </div>
                                        )}
                                    </div>
                                </Link>
                                {!!isOfficialHSEvent && <div className="hs-info" data-testid={dataTestId && `${dataTestId}.position-${position?.id}.player1-hs-info`}>{renderHSInfo(player1)}</div>}
                                <Link to={`/profiles/${player2.id}`} data-testid={dataTestId && `${dataTestId}.position-${position?.id}.player2-link`}>
                                    <div className="d-flex flex-row align-items-baseline">
                                        <div className="name-wrapper" data-testid={dataTestId && `${dataTestId}.position-${position?.id}.player2-name`}>
                                            {titleCase(`${player2.firstName[0]}. ${player2.lastName}`)}
                                        </div>
                                        {showContacts && getContactObj(player2.memberId) && <PhoneInfoPopup {...getContactObj(player2.memberId)} playerId={player2.id} showEditContacts={isCurrentUserRegistered(player2.memberId)} />}
                                        {showRatings && (
                                            <div className="utr-container">
                                                <Utr pbrClass={styles.pbr} player={player2} isProgressionDraw={isProgressionDraw} force={{ ratingType: gameType, utrType, showDecimals }} sportTypeId={sportTypeId} />
                                            </div>
                                        )}
                                    </div>
                                </Link>
                                {!!isOfficialHSEvent && <div className="hs-info" data-testid={dataTestId && `${dataTestId}.position-${position?.id}.player2-hs-info`}>{renderHSInfo(player2)}</div>}
                            </div>
                        </div>
                    )}
                    {!((isPlayer && player && player.id) || (isTeam && player1 && player1.id && player2 && player2.id)) && (
                        <div className="player-details">
                            {gameType === 'singles' && <span className="flag-container" />}
                            <div className={`player-name`}>
                                <span className="name-wrapper" data-testid={dataTestId && `${dataTestId}.position-${position?.id}.tbd-name`}>
                                    {isBye ? 'Bye' : 'TBD'}
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className={`score-card score-card-blank ${styles.scoreCardBlank} ${!!isOfficialHSEvent && 'hs-event'}`}>
            <div className={`card-view ${error ? styles.error : ''}`}>
                <div className="header">
                    <div className="match-data">
                        {!hasBye && showDate
                            ? (
                                <div className="flex-column date" data-testid={dataTestId && `${dataTestId}.date`}>
                                    {date
                                        ? (
                                            <React.Fragment>
                                                {formatMatchDateForEventPage(date, timeZoneValue)}{displayTime ? ` at ${formatMatchTime(date, timeZoneValue)}` : ''}
                                            </React.Fragment>
                                        )
                                        : <React.Fragment>TBD</React.Fragment>}
                                </div>
                            )
                            : <div className="flex-column date">&nbsp;</div>}
                        <div className="flex-column match-status" />
                    </div>
                    <div className="player-data">
                        <div className="draw-tag fs10" data-testid={dataTestId && `${dataTestId}.draw-tag-1`}>{getDrawTag(position1)}</div>
                        {getCardTeam(position1, position1Seed)}
                        {position1?.type?.value === 'team' && <hr className="mt0 mb0 mr16" />}
                        {getCardTeam(position2, position2Seed)}
                        <div className="draw-tag fs10" data-testid={dataTestId && `${dataTestId}.draw-tag-2`}>{getDrawTag(position2)}</div>
                    </div>
                </div>
                <hr className={`divider`} />
                <div className="footer">
                    <div className="flex-grow-1">
                        {renderRoundLabel()} {locationDisplay ? <span data-testid={dataTestId && `${dataTestId}.location`}> • {locationDisplay}</span> : ''}
                    </div>
                    {canPostScore() && (
                        <div className="align-content-end">
                            <button
                                type="button"
                                className={`btn btn-primary btn-sm`}
                                data-testid={dataTestId && `${dataTestId}.post-score-btn`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    matchPostScore();
                                }}
                            >
                                Post Score
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

ScoreCardBlank.defaultProps = {
    showDateTrue: true,
    'data-testid': null,
};

const mapStateToProps = ({ coreUI, auth, events: { contacts } }) => ({
    coreUI,
    auth,
    contacts,
});

export default withRouter(connect(mapStateToProps, null)(ScoreCardBlank));
